import { TranslationKey } from '@tolgee/ngx';
import { getIconPath } from '../../../../functions/get-icon-path';
import { UI_STRINGS } from '../../../../functions/language/uistrings';
import { SubsectorIdentifier } from '../../../../model/enums/emission-icon-identifier';
import { IconColour } from '../../../../model/enums/icon-colour';
import { TransitionTargetSubsector } from '../../../../model/enums/transition-target-subsector';

export interface Subsector {
  icon: string;
  name: string; // same as name in AirTable
  title: TranslationKey;
  type: SubsectorIdentifier;
}

export interface Sector {
  type: TransitionTargetSector;
  title: TranslationKey;
  subsectors: Subsector[];
}

export enum TransitionTargetSector {
  TRANSPORT = 'transport',
  INDUSTRY = 'industry',
  AGRICULTURE = 'agriculture',
  ENERGY = 'energy',
  OTHER = 'other',
}

const imageUrl = (icon: SubsectorIdentifier): string => {
  return getIconPath(icon, IconColour.White);
};

// TODO, all these constanst should be generated by AirTable script and brought in to frontend with climateengine
const FOSSILE_FREE_TRANSPORT_BY_CAR: Subsector = {
  icon: imageUrl(SubsectorIdentifier.PRIVATE_TRANSPORT),
  name: TransitionTargetSubsector.Mobility,
  title: UI_STRINGS.SUBSECTOR_ICONS.PRIVATE_TRANSPORT,
  type: SubsectorIdentifier.PRIVATE_TRANSPORT,
};
const FOSSILE_FREE_FREIGHT_TRANSPORT: Subsector = {
  icon: imageUrl(SubsectorIdentifier.FREIGHT_TRANSPORT),
  name: TransitionTargetSubsector.Freight,
  title: UI_STRINGS.SUBSECTOR_ICONS.FREIGHT_TRANSPORT,
  type: SubsectorIdentifier.FREIGHT_TRANSPORT,
};
const OTHER_TRANSPORT: Subsector = {
  icon: imageUrl(SubsectorIdentifier.OTHER_TRANSPORT),
  name: TransitionTargetSubsector.OtherTransport,
  title: UI_STRINGS.SUBSECTOR_ICONS.OTHER_TRANSPORT,
  type: SubsectorIdentifier.OTHER_TRANSPORT,
};

const IRON_AND_STEEL: Subsector = {
  icon: imageUrl(SubsectorIdentifier.IRON_AND_STEEL),
  name: TransitionTargetSubsector.IronAndSteel,
  title: UI_STRINGS.SUBSECTOR_ICONS.IRON_AND_STEEL,
  type: SubsectorIdentifier.IRON_AND_STEEL,
};
const CEMENT_AND_CONCRETE: Subsector = {
  icon: imageUrl(SubsectorIdentifier.CEMENT_AND_CONCRETE),
  name: TransitionTargetSubsector.CementAndConcrete,
  title: UI_STRINGS.SUBSECTOR_ICONS.CEMENT_AND_CONCRETE,
  type: SubsectorIdentifier.CEMENT_AND_CONCRETE,
};
const REFINERIES: Subsector = {
  icon: imageUrl(SubsectorIdentifier.REFINERIES),
  name: TransitionTargetSubsector.Refineries,
  title: UI_STRINGS.SUBSECTOR_ICONS.REFINERIES,
  type: SubsectorIdentifier.REFINERIES,
};
const CHEMISTRY: Subsector = {
  icon: imageUrl(SubsectorIdentifier.CHEMICAL),
  name: TransitionTargetSubsector.Chemical,
  title: UI_STRINGS.SUBSECTOR_ICONS.CHEMICAL,
  type: SubsectorIdentifier.CHEMICAL,
};
const PAPER: Subsector = {
  icon: imageUrl(SubsectorIdentifier.PAPER),
  name: TransitionTargetSubsector.PaperAndPulp,
  title: UI_STRINGS.SUBSECTOR_ICONS.PAPER,
  type: SubsectorIdentifier.PAPER,
};
const METALS: Subsector = {
  icon: imageUrl(SubsectorIdentifier.METALS),
  name: TransitionTargetSubsector.Metals,
  title: UI_STRINGS.SUBSECTOR_ICONS.METALS,
  type: SubsectorIdentifier.METALS,
};
const OTHER_INDUSTRY: Subsector = {
  icon: imageUrl(SubsectorIdentifier.OTHER_INDUSTRY),
  name: TransitionTargetSubsector.IndustryOther,
  title: UI_STRINGS.SUBSECTOR_ICONS.OTHER_INDUSTRY,
  type: SubsectorIdentifier.OTHER_INDUSTRY,
};

const AGRICULTURAL_LAND: Subsector = {
  icon: imageUrl(SubsectorIdentifier.AGRICULTURAL_LAND),
  name: TransitionTargetSubsector.AgriculturalLand,
  title: UI_STRINGS.SUBSECTOR_ICONS.AGRICULTURAL_LAND,
  type: SubsectorIdentifier.AGRICULTURAL_LAND,
};
const ANIMAL_DIGESTION: Subsector = {
  icon: imageUrl(SubsectorIdentifier.ANIMAL_DIGESTION),
  name: TransitionTargetSubsector.AnimalDigestion,
  title: UI_STRINGS.SUBSECTOR_ICONS.ANIMAL_DIGESTION,
  type: SubsectorIdentifier.ANIMAL_DIGESTION,
};
const STORAGE_OF_FERTILISERS: Subsector = {
  icon: imageUrl(SubsectorIdentifier.STORAGE_OF_FERTILISERS),
  name: TransitionTargetSubsector.StorageOfFertilisers,
  title: UI_STRINGS.SUBSECTOR_ICONS.STORAGE_OF_FERTILISERS,
  type: SubsectorIdentifier.STORAGE_OF_FERTILISERS,
};

const ELECTRICITY_AND_DISTRICT_HEATING: Subsector = {
  icon: imageUrl(SubsectorIdentifier.ELECTRICITY_AND_DISTRICT_HEATING),
  name: TransitionTargetSubsector.ElectricityAndDistrictHeating,
  title: UI_STRINGS.SUBSECTOR_ICONS.ELECTRICITY_AND_DISTRICT_HEATING,
  type: SubsectorIdentifier.ELECTRICITY_AND_DISTRICT_HEATING,
};
const LOCAL_HEATING: Subsector = {
  icon: imageUrl(SubsectorIdentifier.LOCAL_HEATING),
  name: TransitionTargetSubsector.LocalHeating,
  title: UI_STRINGS.SUBSECTOR_ICONS.LOCAL_HEATING,
  type: SubsectorIdentifier.LOCAL_HEATING,
};
const LOCAL_HEATING_RESIDENTIAL: Subsector = {
  icon: imageUrl(SubsectorIdentifier.LOCAL_HEATING_RESIDENTIAL),
  name: TransitionTargetSubsector.LocalHeatingResidential,
  title: UI_STRINGS.SUBSECTOR_ICONS.LOCAL_HEATING_RESIDENTIAL,
  type: SubsectorIdentifier.LOCAL_HEATING_RESIDENTIAL,
};
const LOCAL_COOLING_RESIDENTIAL: Subsector = {
  icon: imageUrl(SubsectorIdentifier.LOCAL_COOLING_RESIDENTIAL),
  name: TransitionTargetSubsector.LocalCoolingResidential,
  title: UI_STRINGS.SUBSECTOR_ICONS.LOCAL_COOLING_RESIDENTIAL,
  type: SubsectorIdentifier.LOCAL_COOLING_RESIDENTIAL,
};
const LOCAL_COOLING: Subsector = {
  icon: imageUrl(SubsectorIdentifier.LOCAL_COOLING),
  name: TransitionTargetSubsector.LocalCooling,
  title: UI_STRINGS.SUBSECTOR_ICONS.LOCAL_COOLING,
  type: SubsectorIdentifier.LOCAL_COOLING,
};
const LOCAL_OTHER: Subsector = {
  icon: imageUrl(SubsectorIdentifier.LOCAL_OTHER),
  name: TransitionTargetSubsector.LocalOther,
  title: UI_STRINGS.SUBSECTOR_ICONS.LOCAL_OTHER,
  type: SubsectorIdentifier.LOCAL_OTHER,
};
const LOCAL_OTHER_RESIDENTIAL: Subsector = {
  icon: imageUrl(SubsectorIdentifier.LOCAL_OTHER_RESIDENTIAL),
  name: TransitionTargetSubsector.LocalOtherResidential,
  title: UI_STRINGS.SUBSECTOR_ICONS.LOCAL_OTHER_RESIDENTIAL,
  type: SubsectorIdentifier.LOCAL_OTHER_RESIDENTIAL,
};

const SOLVENTS: Subsector = {
  icon: imageUrl(SubsectorIdentifier.SOLVENTS),
  name: TransitionTargetSubsector.Solvents,
  title: UI_STRINGS.SUBSECTOR_ICONS.SOLVENTS,
  type: SubsectorIdentifier.SOLVENTS,
};
const MACHINERY: Subsector = {
  icon: imageUrl(SubsectorIdentifier.MACHINERY),
  name: TransitionTargetSubsector.Machinery,
  title: UI_STRINGS.SUBSECTOR_ICONS.MACHINERY,
  type: SubsectorIdentifier.MACHINERY,
};
const WASTE: Subsector = {
  icon: imageUrl(SubsectorIdentifier.WASTE),
  name: TransitionTargetSubsector.Waste,
  title: UI_STRINGS.SUBSECTOR_ICONS.WASTE,
  type: SubsectorIdentifier.WASTE,
};

const TRANSPORT_SECTOR: Sector = {
  type: TransitionTargetSector.TRANSPORT,
  title: UI_STRINGS.SECTORS.TRANSPORT,
  subsectors: [FOSSILE_FREE_TRANSPORT_BY_CAR, FOSSILE_FREE_FREIGHT_TRANSPORT, OTHER_TRANSPORT],
};
const INDUSTRY_SECTOR: Sector = {
  type: TransitionTargetSector.INDUSTRY,
  title: UI_STRINGS.SECTORS.INDUSTRY,
  subsectors: [IRON_AND_STEEL, CEMENT_AND_CONCRETE, REFINERIES, CHEMISTRY, PAPER, METALS, OTHER_INDUSTRY],
};
const AGRICULTURE_SECTOR: Sector = {
  type: TransitionTargetSector.AGRICULTURE,
  title: UI_STRINGS.SECTORS.AGRICULTURE,
  subsectors: [AGRICULTURAL_LAND, ANIMAL_DIGESTION, STORAGE_OF_FERTILISERS],
};
const ENERGY_SECTOR: Sector = {
  type: TransitionTargetSector.ENERGY,
  title: UI_STRINGS.SECTORS.ENERGY,
  subsectors: [
    ELECTRICITY_AND_DISTRICT_HEATING,
    LOCAL_HEATING,
    LOCAL_HEATING_RESIDENTIAL,
    LOCAL_COOLING_RESIDENTIAL,
    LOCAL_COOLING,
    LOCAL_OTHER,
    LOCAL_OTHER_RESIDENTIAL,
  ],
};
const OTHER_SECTOR: Sector = {
  type: TransitionTargetSector.OTHER,
  title: UI_STRINGS.SECTORS.OTHER,
  subsectors: [SOLVENTS, MACHINERY, WASTE],
};

export const SECTORS: Sector[] = [TRANSPORT_SECTOR, INDUSTRY_SECTOR, AGRICULTURE_SECTOR, ENERGY_SECTOR, OTHER_SECTOR];

export const ALL_SUBSECTORS: Subsector[] = [
  ...TRANSPORT_SECTOR.subsectors,
  ...INDUSTRY_SECTOR.subsectors,
  ...AGRICULTURE_SECTOR.subsectors,
  ...ENERGY_SECTOR.subsectors,
  ...OTHER_SECTOR.subsectors,
];
